import { Flex, Image, Link, useDisclosure } from '@chakra-ui/react';
import { useFrontierAuth } from '../../util/auth/useFrontierAuth';
import {
  BaseButton,
  BaseNav,
  RouteAuthenticationRequirement,
  SupportButton,
  useNavItemStore
} from '@cks-internal/react-base';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import ApplicationRoutes from '../../constants/applicationRoutes.ts';
import logo from '../../assets/logo-small.png';
import { AnnouncementBox } from '../crmAdmin/announcements/banner/AnnouncementBox';
import { NewActivityDialog } from '../../features/activities/editActivity/newActivityDialog';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { WorkflowConnection } from '../../features/workflow/hub/workflowSocketProvider';
import { WfTool } from '../../features/workflow/WfTool';
import { ApplicationRoles } from '../../constants/applicationRoles.ts';
import { useNavigate } from 'react-router-dom';
import { usePerformanceFlagStore } from '../../stores/performanceFlagStore.ts';
import { PerformanceSettings } from '../../features/header/PerformanceSettings.tsx';
import { useTutorialsLocalStorage } from '../../util/tutorials/useTutorialsLocalStorage';
import { PerformanceSettingsTutorialKey } from './utils/performanceSettingsTutorialConstants';
import { API_URL } from '../../lib/config.ts';

// Map of nav item text to their correct allowed roles
// This is needed because buildNavItemFromRoute incorrectly inherits parent roles to children
const navItemRoles: Record<string, string[]> = {
  'Agent Activity Report': [ApplicationRoles.systemAdmin, ApplicationRoles.userAdmin, ApplicationRoles.activityAdmin],
  'Agent Folio Report': [ApplicationRoles.systemAdmin, ApplicationRoles.userAdmin, ApplicationRoles.activityAdmin],
  'Booking Report': [
    ApplicationRoles.systemAdmin,
    ApplicationRoles.activityAdmin,
    ApplicationRoles.crm,
    ApplicationRoles.accounting
  ],
  'Checkout Report': [
    ApplicationRoles.systemAdmin,
    ApplicationRoles.activityAdmin,
    ApplicationRoles.crm,
    ApplicationRoles.accounting
  ],
  'Invoicing Group': [
    ApplicationRoles.systemAdmin,
    ApplicationRoles.activityAdmin,
    ApplicationRoles.crm,
    ApplicationRoles.accounting
  ],
  'Issue Tracking Report': [ApplicationRoles.systemAdmin, ApplicationRoles.userAdmin, ApplicationRoles.activityAdmin],
  'Reconciliation Report': [ApplicationRoles.systemAdmin]
};

type Props = {
  forceUnauthenticatedNav?: boolean;
};
export const NavBar = (props: Props) => {
  const newActivityDisclosure = useDisclosure();
  const auth = useFrontierAuth();
  const performanceFlagStore = usePerformanceFlagStore();

  const nav = useNavigate();
  const routeNav = useNavItemStore((state) => {
    return { items: state.getDefault(), get: state.get };
  });

  const isStaff = useMemo(() => {
    return auth.isAuthenticated && auth.isInRole([ApplicationRoles.staff]);
  }, [auth.user, auth.isAuthenticated]);

  // Filter navigation items based on user roles
  const filteredNavItems = useMemo(() => {
    const filterNavItem = (item: BaseNav.NavItem): BaseNav.NavItem | null => {
      // Filter out items without text (they shouldn't appear in nav)
      if (!item.text) {
        return null;
      }

      // Use the correct roles from our mapping if available, otherwise use the item's roles
      const correctRoles = navItemRoles[item.text] || item.allowedRoles;

      // Check if user has required roles for this item
      if (correctRoles && correctRoles.length > 0) {
        if (!auth.isInRole(correctRoles)) {
          return null;
        }
      }

      // If no sub-items, return the item as-is
      if (!item.subItems || item.subItems.length === 0) {
        return item;
      }

      // Filter sub-items recursively
      const filteredSubItems = item.subItems
        .map(filterNavItem)
        .filter((subItem): subItem is BaseNav.NavItem => subItem !== null);

      // If no sub-items remain after filtering, hide the parent item too
      if (filteredSubItems.length === 0) {
        return null;
      }

      // Return item with filtered sub-items
      return {
        ...item,
        subItems: filteredSubItems
      };
    };

    return routeNav.items.map(filterNavItem).filter((item): item is BaseNav.NavItem => item !== null);
  }, [routeNav.items, auth.user]);

  useEffect(() => {
    if (auth.isAuthenticated && !isStaff) {
      nav(ApplicationRoutes.account.accessDenied);
    }
  }, [isStaff]);

  const profileItems: BaseNav.NavItem = useMemo(() => {
    const subItems: BaseNav.NavItem[] = [
      routeNav.get(ApplicationRoutes.account.myProfile),
      routeNav.get(ApplicationRoutes.account.logout)
    ];
    return {
      text: 'Profile',
      subItems: subItems,
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: []
    };
  }, []);

  if (!isStaff || props.forceUnauthenticatedNav) {
    return (
      <BaseNav.Header
        logo={
          <Flex>
            <Link href='/'>
              <Image width={'40px'} height={['40px']} src={logo || undefined} alt='Frontier Lodging Solutions' />
            </Link>
          </Flex>
        }
        navItems={filteredNavItems}
        profileItems={profileItems}
        avatarName={''}
      />
    );
  }

  return (
    <>
      <BaseNav.Header
        profileItems={profileItems}
        beforeNavChildren={
          auth.isAuthenticated && (
            <Flex gap={'8px'} ml={'auto'}>
              {(window.location.hostname === 'localhost' || window.location.hostname.includes('dev.cksoftware.ca')) && (
                <BaseButton
                  size='xs'
                  variant='secondary'
                  onClick={() => window.open(`${API_URL}/dev/imap`, '_blank', 'noopener,noreferrer')}>
                  IMAP Service
                </BaseButton>
              )}
              <PerformanceSettings />
              <SupportButton useDisplayMedia={true} buttonProps={{ variant: 'secondary' }} />
              {auth.isInRole([ApplicationRoles.reservations]) && (
                <BaseButton
                  borderRadius={'lg'}
                  toolTip='New Activity'
                  size={'xs'}
                  onClick={newActivityDisclosure.onOpen}
                  icon={faPlus}>
                  New Activity
                </BaseButton>
              )}
            </Flex>
          )
        }
        logo={
          <Flex>
            <Link href='/'>
              <Image width={'40px'} height={['40px']} src={logo || undefined} alt='Frontier Lodging Solutions' />
            </Link>
          </Flex>
        }
        navItems={filteredNavItems}
        avatarName={`${(auth.user?.FirstName || '') + ' ' + (auth.user?.LastName || '')}`.trim()}>
        <Flex gap={'8px'}>
          {auth.user?.EnableWorkflowSystem && !performanceFlagStore.disableWorkflow && (
            <WorkflowConnection children={<WfTool />} />
          )}
        </Flex>
      </BaseNav.Header>
      {auth.isAuthenticated && <AnnouncementBox />}
      <NewActivityDialog disclosure={newActivityDisclosure} />
      <LazyPerformanceSettingsTutorial />
    </>
  );
};

const PerformanceSettingsTutorialComponent = lazy(() => import('./utils/performanceSettingsTutorial'));
const LazyPerformanceSettingsTutorial = () => {
  const auth = useFrontierAuth();
  const tutorials = auth.user?.Tutorials;
  const tutorialsStorage = useTutorialsLocalStorage();
  const doneLocally = tutorialsStorage.has(PerformanceSettingsTutorialKey);

  if (auth.user?.UserId == undefined || doneLocally || tutorials?.includes(PerformanceSettingsTutorialKey)) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <PerformanceSettingsTutorialComponent userId={auth.user.UserId} />
    </Suspense>
  );
};
